// Guest boot MUST be the first import so GUEST_MODE + egress guards are
// installed before App / supabase / analytics modules evaluate.
import { GUEST_MODE } from '@/guest/boot'
import AppLayout from '@/layouts/AppLayoutApp.vue'
// import { Logtail } from '@logtail/browser'
import { VueQueryPlugin } from '@tanstack/vue-query'
import { createPinia } from 'pinia'
import { createApp } from 'vue'
import { inject } from '@vercel/analytics'
import App from './App.vue'
import { queryClient } from './lib/queryClient'
import router from './router'
import { installPublicPageTracking } from '@/analytics/track'
import { logBuildInfo } from './utils/logBuildInfo'
import { installDevLongFrames } from './utils/devLongFrames'
import './utils/installPrompt'
import { reportClientError } from './utils/errorReporting'
import { isBenignResizeObserverLoopError } from './utils/resizeObserver'
import { isProductionEnv } from './utils/env'
import { captureLogRocketException, scheduleLogRocketInit } from './utils/logRocket'
import { installPress } from './directives/press'
import { initBackButton } from './native/backStack'
import { initNativeApp } from './native/nativeApp'
import { companionPolicy } from './native/companion'
import { hideSplashAfterFirstPaint } from './native/splash'

logBuildInfo()

if (typeof history !== 'undefined' && 'scrollRestoration' in history) {
    history.scrollRestoration = 'manual'
}
import './assets/main.css'
import './assets/tailwind.css'
import './assets/theme.css'
import './assets/inbox.css'
import './assets/agent.css'
import './assets/liquid-glass.css'
import './assets/project.css'
import './assets/crm.css'
import './assets/harness-theme.css'
import './assets/motion.css'
// Desktop app window chrome; inert outside `html.desktop-app`. Last, so it wins ties.
import './desktop/desktop.css'

const app = createApp(App)

// let logtail: Logtail | null = null

// if (import.meta.env.VITE_APP_LOGTAIL_TOKEN) {
//     logtail = new Logtail(import.meta.env.VITE_APP_LOGTAIL_TOKEN)
// }

app.use(createPinia())
app.use(VueQueryPlugin, { queryClient })
app.use(router)
installPress(app)
// Android back closes the topmost sheet / menu first (src/native/backStack.ts). Native only.
void initBackButton(router)
// Marketing-page attribution. The companion app has no marketing pages and runs no marketing analytics.
if (!GUEST_MODE && companionPolicy.allowMarketingPixels) {
    installPublicPageTracking(router)
}
// Native only: app URLs (quick actions, deep links), lifecycle, push, network, in-app browser
// (src/native/nativeApp.ts). Before mount, so the app-URL listener receives the cold-start launch URL.
if (!GUEST_MODE) {
    initNativeApp(router)
}

// Vercel Analytics stays production-only so staging sessions don't pollute
// prod metrics. LogRocket also records staging for session replay / debugging.
// Neither runs in the companion app (src/native/companion.ts; LogRocket checks it itself).
if (!GUEST_MODE && isProductionEnv() && companionPolicy.allowMarketingPixels) {
    inject()
}
if (!GUEST_MODE) {
    scheduleLogRocketInit()
}

app.config.errorHandler = function (err: any, vm, info) {
    console.error('Global error:', err)
    console.log('Vue instance:', vm)
    console.log('Error info:', info)
    captureLogRocketException(err, { info })
    if (!GUEST_MODE) {
        reportClientError(err, { type: 'vue', info })
    }
    // if (logtail) {
    //     logtail.error(err)
    //     logtail.log(info)
    //     logtail.flush()
    // }
    return null
}

if (typeof window !== 'undefined' && !GUEST_MODE) {
    window.addEventListener(
        'error',
        (event) => {
            const msg =
                event.error instanceof Error
                    ? event.error.message
                    : typeof event.message === 'string'
                      ? event.message
                      : ''
            if (isBenignResizeObserverLoopError(msg)) {
                event.stopImmediatePropagation()
                event.preventDefault()
                return
            }
            reportClientError(event.error ?? event.message, {
                type: 'window',
                filename: event.filename,
                lineno: event.lineno,
                colno: event.colno,
            })
        },
        true,
    )

    window.addEventListener('unhandledrejection', (event) => {
        reportClientError(event.reason, { type: 'unhandledrejection' })
    })
}

app.component('AppLayout', AppLayout).mount('#app')
// The native splash fades out once the first route has painted (src/native/splash.ts).
hideSplashAfterFirstPaint(router)

if (import.meta.env.DEV && typeof window !== 'undefined') {
    ;(window as Window & { __lhRouter?: typeof router }).__lhRouter = router
    installDevLongFrames()
}

// export { logtail }
